// src/components/pages/Caixa/modules/useCaixaModule.js
import { useCallback, useMemo, useEffect } from 'react';
import * as FinanceiroAPI from '../../../../api/financeiro';
/**
 * useCaixaModule
 * - Centraliza estado do cabeçalho do Caixa
 * - Centraliza handlers (change/focus/blur)
 * - Centraliza lookups (optionsContaCaixa/optionsSituacao/optionsColaborador)
 *
 * Mantém o contrato esperado pelo CaixaTabPanel:
 *   module.state + module.actions
 */
export function useCaixaModule({
    dados,
    setDados,
    consultaDatalist,
    setColaborador,

    // coisas do CreateOrEdit
    btnText,

    // opções externas (se preferir: passam pelo module)
    optionsColaborador = [],
    Colaborador,
    optionsContaCaixa = [],
    optionsEmpresa = [],
    optionsSituacao = [],

    // opcional: para mensagens no blur/validações
    //setFlashMessage,
    //activeTabKey
}) {
    const carregarTotaisCaixa = useCallback(async () => {
        if (!dados?.id) return;

        try {
            const result = await FinanceiroAPI.totalRecebidoByCaixaId(dados.id);

            setDados((prev) => ({
                ...prev,
                totalRecebido: result?.totalRecebido != null ? Number(result.totalRecebido).toFixed(2) : prev.totalRecebido,
                totalPago: result?.totalPago != null ? Number(result.totalPago).toFixed(2) : prev.totalPago,
                totaisPorForma: result?.totaisPorForma || {},
            }));
        } catch (e) {
            console.error(e);
        }
    }, [dados?.id, setDados]);

    const onEnterTab = useCallback(async () => {
        await carregarTotaisCaixa(); // função que busca dados atualizados
    }, [carregarTotaisCaixa]);

    useEffect(() => {
        carregarTotaisCaixa();
    }, [dados?.id, carregarTotaisCaixa]);

    // helper
    const getIdByValueDom = useCallback((value) => {
        return document.querySelector(`datalist option[value="${value}"]`)?.dataset.id || null;
    }, []);
    const buscaSaldoByContaId = useCallback(async (contaId) => {
        if (!contaId) return null;
        const r = await FinanceiroAPI.buscaSaldoByContaId(contaId);
        return r?.data?.saldo ?? r?.saldo ?? null; // tenta cobrir formatos diferentes
    }, []);
    /**
     * change genérico:
     * - mantém compatibilidade com name "Colaborador.descricao"
     * - checkbox usa e.target.checked
     */
    const change = useCallback(
        async (e) => {
            if (!e?.target) return;

            const { name, type, value, checked } = e.target;
            const nextValue = type === 'checkbox' ? checked : value;

            // ✅ 1) Conta do Caixa
            if (name === 'contaId') {
                const saldo = await buscaSaldoByContaId(nextValue);

                setDados((prev) => ({
                    ...prev,
                    contaId: nextValue,
                    saldoAbertura: saldo != null ? Number(saldo).toFixed(2) : prev.saldoAbertura,
                }));
                return;
            }

            // ✅ 2) Situação do Caixa
            if (name === 'situacaoId') {
                // define primeiro a situação
                setDados((prev) => ({ ...prev, situacaoId: nextValue }));

                // se quiser: só calcula fechamento quando sai de "Aberto" (1)
                const situacaoNum = Number(nextValue);
                const vaiFechar = situacaoNum !== 1;

                if (vaiFechar) {
                    const contaId = typeof value !== 'undefined' ? null : null; // ignore, vamos pegar do state
                    const saldo = await buscaSaldoByContaId(dados?.contaId ?? null);

                    setDados((prev) => ({
                        ...prev,
                        situacaoId: nextValue,
                        saldoFechamento: saldo != null ? Number(saldo).toFixed(2) : prev.saldoFechamento,
                        fechamento: new Date(),
                    }));
                }

                return;
            }

            // suporte a "Colaborador.descricao" sem quebrar o objeto
            if (name?.startsWith('Colaborador.')) {
                const [root, field] = name.split('.');
                setColaborador((prev) => ({ ...prev, [field]: nextValue }));
                consultaDatalist({
                    separador: 'colaborador',
                    empresaId: dados.empresaId,
                    filtro: value,
                    ativo: true,
                    tipo: 'vendaInterna',
                });
                const selectedId = getIdByValueDom(value);

                setDados((prev) => ({ ...prev, colaboradorId: selectedId }));
                return;
            }

            setDados((prev) => ({
                ...prev,
                [name]: nextValue,
            }));
        },
        [setDados, buscaSaldoByContaId, dados?.contaId]
    );

    // focus/blur: por enquanto não muda nada (mas mantém contrato)
    const focus = useCallback(() => {}, []);
    const blur = useCallback(() => {}, []);

    // state e actions no formato padrão que você já está usando
    const state = useMemo(
        () => ({
            dados,
            btnText,

            //options
            optionsColaborador,
            optionsContaCaixa,
            optionsEmpresa,
            optionsSituacao,

            Colaborador,
        }),
        [dados, btnText, optionsContaCaixa, optionsColaborador, Colaborador]
    );

    const actions = useMemo(
        () => ({
            change,
            focus,
            blur,
            onEnterTab,
        }),
        [change, focus, blur]
    );

    return { state, actions };
}
